import * as React from "react";
import styled from "@emotion/styled";
import BootstrapModal from "react-bootstrap/Modal";
import { EaselButton, Variant } from "./EaselButton";
import { easelFontFamily, designTokens } from "../../helpers/constants";
import { Close } from "../../icons/Close";
import { ErrorAlert } from "./ErrorAlert";

interface ModalProps {
  show: boolean;
  onCancel: () => void;
  onSave?: () => void;
  header: string | React.ReactNode;
  children: React.ReactNode;
  cancelButtonText?: string;
  saveButtonText?: string;
  cancelButtonVariant?: Variant;
  saveButtonVariant?: Variant;
  saveButtonDisabled?: boolean;
  fullScreen?: boolean;
  subHeader?: string;
  backgroundColor?: string;
  footer?: string | React.ReactNode;
  error?: string;
}

export const DSModal: React.FC<ModalProps> = ({
  show,
  header,
  onCancel,
  onSave,
  children,
  cancelButtonText,
  saveButtonText,
  cancelButtonVariant = "secondary",
  saveButtonVariant = "secondary",
  saveButtonDisabled = false,
  fullScreen = false,
  subHeader,
  backgroundColor,
  footer,
  error,
}: ModalProps) => {
  return (
    <AssessmentModal
      fullScreen={fullScreen}
      onHide={onCancel}
      show={show}
      centered
    >
      <Header fullScreen={fullScreen}>
        <Titles>
          {header}
          <div>{subHeader}</div>
          {error && <ErrorAlert variant="danger">{error}</ErrorAlert>}
        </Titles>

        <CloseButton onClick={onCancel}>
          <Close />
        </CloseButton>
      </Header>
      <Body fullScreen={fullScreen} backgroundColor={backgroundColor}>
        {children}
      </Body>
      <Footer withFooter={footer !== undefined} fullScreen={fullScreen}>
        {footer || null}
        <ButtonContainer>
          {cancelButtonText && (
            <EaselButton
              onClick={onCancel}
              variant={cancelButtonVariant}
              size="sm"
              buttonType="outline"
            >
              {cancelButtonText}
            </EaselButton>
          )}
          {saveButtonText && (
            <EaselButton
              onClick={onSave}
              variant={saveButtonVariant}
              size="sm"
              disabled={saveButtonDisabled}
            >
              {saveButtonText}
            </EaselButton>
          )}
        </ButtonContainer>
      </Footer>
    </AssessmentModal>
  );
};

const AssessmentModal = styled(BootstrapModal, {
  shouldForwardProp: (prop) => prop !== "fullScreen",
})((props: { fullScreen: boolean }) => ({
  zIndex: 1090,
  fontFamily: easelFontFamily,

  ".modal-dialog": props.fullScreen
    ? {
        maxWidth: "95%",
      }
    : {},
}));

const Header = styled(BootstrapModal.Header, {
  shouldForwardProp: (prop) => prop !== "fullScreen",
})((props: { fullScreen: boolean }) => ({
  display: "flex",
  padding: props.fullScreen ? "1rem 1.75rem" : "1rem 1.5rem",
}));

const Titles = styled.div({
  fontSize: designTokens["ds-font-size-lg"],
  fontWeight: 500,
  display: "flex",
  flexDirection: "column",
  width: "100%",

  div: {
    fontSize: "1rem",
    fontWeight: 400,
  },
});

const Footer = styled(BootstrapModal.Footer, {
  shouldForwardProp: (prop) =>
    !(prop === "fullScreen" || prop === "withFooter"),
})((props: { fullScreen: boolean; withFooter: boolean }) => ({
  padding: props.fullScreen ? "1rem" : "1.5rem",
  justifyContent: props.withFooter ? "space-between" : "flex-end",
}));

const Body = styled(BootstrapModal.Body, {
  shouldForwardProp: (prop) =>
    !(prop === "backgroundColor" || prop === "fullScreen"),
})((props: { backgroundColor?: string; fullScreen: boolean }) => ({
  padding: props.fullScreen ? "0 1rem" : "1.5rem",
  background: props.backgroundColor || "unset",
  p: {
    fontFamily: easelFontFamily,
  },
}));

const CloseButton = styled.span({
  cursor: "pointer",
});

const ButtonContainer = styled.div({
  display: "flex",
  width: "max-content",
  alignItems: "center",

  button: {
    margin: "0 0.25rem",
  },
});
